import { toast } from 'sonner';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { setAuthSnapshotProvider } from '@app/routes';

import { getHttpClient, installInterceptors, type AuthBridge } from '@shared/services';

import { authService } from '../services';
import type { AuthUser } from '../validations';

interface AuthState {
  readonly user: AuthUser | null;
  readonly accessToken: string | null;
  readonly refreshToken: string | null;
  readonly isHydrated: boolean;
}

interface AuthActions {
  setSession: (payload: {
    readonly user: AuthUser;
    readonly accessToken: string;
    readonly refreshToken: string;
  }) => void;
  setTokens: (payload: { readonly accessToken: string; readonly refreshToken: string }) => void;
  clearSession: () => void;
  setHydrated: () => void;
}

export type AuthStore = AuthState & AuthActions;

const STORAGE_KEY = 'lwart.auth';

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isHydrated: false,
      setSession: ({ user, accessToken, refreshToken }) => {
        set({ user, accessToken, refreshToken });
      },
      setTokens: ({ accessToken, refreshToken }) => {
        set({ accessToken, refreshToken });
      },
      clearSession: () => {
        set({ user: null, accessToken: null, refreshToken: null });
      },
      setHydrated: () => {
        set({ isHydrated: true });
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHydrated();
      },
    },
  ),
);

let _onSessionExpired: (() => void) | null = null;

export function registerSessionExpiredHandler(handler: () => void): void {
  _onSessionExpired = handler;
}

export function initializeAuthStore(): void {
  setAuthSnapshotProvider(() => {
    const user = useAuthStore.getState().user;
    return { user: user !== null ? { id: user.id } : null };
  });

  installInterceptors(getHttpClient(), authBridge);
}

const authBridge: AuthBridge = {
  getAccessToken: () => useAuthStore.getState().accessToken,
  getRefreshToken: () => useAuthStore.getState().refreshToken,
  refresh: async (refreshToken) => {
    const response = await authService.refresh(refreshToken);
    return response;
  },
  setTokens: (tokens) => {
    useAuthStore.getState().setTokens(tokens);
  },
  clearSession: () => {
    useAuthStore.getState().clearSession();
  },
  onSessionExpired: () => {
    useAuthStore.getState().clearSession();
    toast.error('Sessão expirada', { description: 'Faça login novamente para continuar.' });
    if (_onSessionExpired !== null) {
      _onSessionExpired();
    } else {
      window.location.href = '/login';
    }
  },
};
